import { Typography } from "@mui/material";
import { useContext, useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDrop, useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import { NavLink } from "../../../../common/components";
import { AppToolbarContext } from "../../../../context/AppToolbar";
import {
  DND_ITEM_TYPES,
  DraggedDesignItem,
  DraggedProjectItem,
} from "../../../../constant/dnd.constant";
import { palette } from "../../../../styles/theme";

interface Props {
  label: string;
  labelKey?: string;
  url: string;
  projectId?: string;
  teamId?: string;
  teamName?: string;
  onDesignDrop?: (designId: string, targetProjectId: string) => Promise<void>;
  onProjectDrag?: (projectId: string, sourceTeamId: string) => void;
}

export const ProjectNavLink = (props: Props) => {
  const { t } = useTranslation();
  const {
    label,
    labelKey,
    url,
    projectId,
    teamId,
    teamName,
    onDesignDrop,
    onProjectDrag,
  } = props;
  const navigate = useNavigate();
  const { toggleNavigationDrawerOpen } = useContext(AppToolbarContext);
  const [isDropping, setIsDropping] = useState(false);

  const content = useMemo(() => {
    if (label) {
      return label;
    }

    if (labelKey) {
      return t(labelKey);
    }

    return "";
  }, [label, labelKey, t]);

  const [{ isDragging }, drag, preview] = useDrag<
    DraggedProjectItem,
    void,
    { isDragging: boolean }
  >({
    type: DND_ITEM_TYPES.PROJECT,
    item: () => {
      if (onProjectDrag && projectId && teamId) {
        onProjectDrag(projectId, teamId);
      }
      return {
        type: DND_ITEM_TYPES.PROJECT,
        projectId: projectId || "",
        projectName: label,
        teamId: teamId || "",
        teamName: teamName || "",
        source: "navlink",
      };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: !!projectId && !!teamId,
  });

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  const [{ isOver, canDrop }, drop] = useDrop<
    DraggedDesignItem,
    void,
    { isOver: boolean; canDrop: boolean }
  >({
    accept: DND_ITEM_TYPES.DESIGN_CARD,
    canDrop: (item) => {
      return !!projectId && item.projectId !== projectId;
    },
    drop: (item) => {
      if (projectId && onDesignDrop) {
        setIsDropping(true);
        onDesignDrop(item.designId, projectId)
          .catch((error) => {
            console.error("❌ Error dropping design:", error);
          })
          .finally(() => {
            setIsDropping(false);
          });
      }
    },
    collect: (monitor) => {
      return {
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      };
    },
  });

  const isActive = isOver && canDrop;

  const combinedRef = (el: HTMLDivElement | null) => {
    drag(el as any);
    drop(el as any);
  };

  return (
    <div
      ref={combinedRef}
      style={{
        width: "100%",
        minHeight: "32px",
        transition: "all 0.2s ease",
        opacity: isDragging ? 0.4 : 1,
        cursor: projectId && teamId ? "grab" : "default",
        ...(isActive && {
          boxShadow: `0px 0px 0px 1px ${palette.blue.secondary}`,
          borderRadius: "4px",
        }),
      }}
    >
      <NavLink
        content={
          <Typography
            variant="actionSmallIcon"
            maxWidth={{ xs: "230px", lg: "202px" }}
            textOverflow={"ellipsis"}
            whiteSpace={"nowrap"}
            overflow={"hidden"}
          >
            {content}
          </Typography>
        }
        to={url}
        onClick={() => {
          navigate(url);
          toggleNavigationDrawerOpen?.();
        }}
        clickableLink
        style={{
          transition: "all 0.2s ease",
          ...(isDropping && {
            opacity: 0.6,
          }),
        }}
      />
    </div>
  );
};
